import * as styles from "./BurgerMenuMobile.module.scss";

import cx from "clsx";
import React, { useEffect, useState } from "react";
import { DataLayerWrapper } from "../../../../components/DataLayerWrapper/DataLayerWrapper";
import { Arrow } from "../../../../components/common/Icons";
import LinkNoStyle from "../../../../components/common/LinkNoStyle/LinkNoStyle";
import { useHeaderHeight } from "../../../../hooks/useHeaderHeight";
import useWindowHeight from "../../../../hooks/useWindowHeight";
import { Colours } from "../../../../utils/colours";
import { isSpecificSite } from "../../../../utils/isSpecificSite";

export interface HeaderProps {
	show: boolean;
	nestedLinks: DogsTrust.MenuItem[];
}

export const BurgerMenuMobile: React.FC<HeaderProps> = ({
	show,
	nestedLinks,
}) => {
	isSpecificSite("ie", "BurgerMenuMobile");
	const [secondMenuToShow, setSecondMenuToShow] = useState<string>();
	const [thirdMenuToShow, setThirdMenuToShow] = useState<string>();

	const headerHeight = useHeaderHeight();
	const windowHeight = useWindowHeight();
	const navHeight = windowHeight - headerHeight;

	// Change focus for screen readers when navigating between different levels

	// -- Shift focus to first link on menu level 1
	useEffect(() => {
		if (!secondMenuToShow && !thirdMenuToShow) {
			document.getElementById("primary-0")?.focus();
		}
	}, [secondMenuToShow, thirdMenuToShow]);

	// -- Shift focus to first link on menu level 2
	useEffect(() => {
		if (secondMenuToShow?.length > 0 && !thirdMenuToShow) {
			document.getElementById("secondary-0").focus();
		}
	}, [secondMenuToShow, thirdMenuToShow]);

	// -- Shift focus to first link on menu level 3
	useEffect(() => {
		if (thirdMenuToShow?.length > 0) {
			document.getElementById("tertiary-0").focus();
		}
	}, [thirdMenuToShow]);

	return (
		<nav
			className={show ? styles.burgermenucontainer : styles.burgermenuhidden}
			style={{
				top: headerHeight,
				height: show ? navHeight : 0,
			}}
			aria-hidden={!show}
		>
			<ul
				className={styles.primarynavlist}
				aria-haspopup={true}
				aria-expanded={!!secondMenuToShow}
			>
				{nestedLinks.map((primaryLink, primaryIterator) => (
					<li
						key={primaryLink.title}
						className={
							secondMenuToShow === primaryLink.title
								? styles.primarylinkactive
								: styles.primarylink
						}
					>
						<DataLayerWrapper
							event={"menu_navigation"}
							menu_level={"top"}
							menu_clicked={primaryLink.title}
							menu_url={primaryLink.url}
						>
							<LinkNoStyle
								tabIndex={show ? 0 : -1}
								id={`primary-${primaryIterator}`}
								to={primaryLink.url}
								visible={!secondMenuToShow}
								onKeyDown={(e) => {
									if (e.key === "Escape") {
										setSecondMenuToShow(null);
										setThirdMenuToShow(null);
									}
									if (e.key === "Enter") {
										setSecondMenuToShow(primaryLink.title);
										setThirdMenuToShow(null);
									}
									if (
										e.key === "Tab" &&
										!e.shiftKey &&
										primaryIterator === nestedLinks.length - 1 &&
										primaryLink.secondLevelItems?.length === 0
									) {
										e.preventDefault();
										document.getElementById("burger-menu").focus();
									}
								}}
							>
								{primaryLink.title}
							</LinkNoStyle>
						</DataLayerWrapper>
						{primaryLink.secondLevelItems?.length > 0 && (
							<button
								type="button"
								onClick={() => {
									setSecondMenuToShow(primaryLink.title);
									setThirdMenuToShow(null);
								}}
								tabIndex={show ? 0 : -1}
								onKeyDown={(e) => {
									if (e.key === "Escape") {
										setSecondMenuToShow(null);
										setThirdMenuToShow(null);
									}
									if (e.key === "Enter") {
										setSecondMenuToShow(primaryLink.title);
										setThirdMenuToShow(null);
									}
									if (
										e.key === "Tab" &&
										!e.shiftKey &&
										primaryIterator === nestedLinks.length - 1
									) {
										e.preventDefault();
										document.getElementById("burger-menu").focus();
									}
								}}
								style={{
									visibility: secondMenuToShow ? "hidden" : "visible",
								}}
								aria-label={`See more links for ${primaryLink.title}`}
								aria-hidden={!!secondMenuToShow}
							>
								<Arrow
									className={styles.subMenuNavArrows}
									rotation={180}
									withBorder={false}
									height={"24px"}
									fill={
										secondMenuToShow === primaryLink.title
											? Colours.DAWN
											: Colours.SUNSHINE_YELLOW
									}
								/>
							</button>
						)}
						{/* THE SECONDARY NAV MENU */}
						{primaryLink.secondLevelItems?.length > 0 && (
							<div
								className={
									secondMenuToShow === primaryLink.title
										? styles.secondarynavwrapper
										: styles.secondarynavhidden
								}
								style={{
									top: headerHeight,
									left: thirdMenuToShow ? 0 : "50%",
								}}
								aria-haspopup={true}
								aria-expanded={!!thirdMenuToShow}
							>
								<span className={styles.secondarytitle}>
									<button
										type="button"
										onClick={() => {
											setSecondMenuToShow(null);
										}}
										onKeyDown={(e) => {
											if (e.key === "Escape") {
												setSecondMenuToShow(null);
												setThirdMenuToShow(null);
											}
											if (e.key === "Enter") {
												e.preventDefault();
												setSecondMenuToShow(null);
												document.getElementById("burger-menu").focus();
											}
										}}
										className={cx(styles.backarrow, styles.subMenuNavArrows)}
										tabIndex={
											secondMenuToShow === primaryLink.title && !thirdMenuToShow
												? 0
												: -1
										}
										id={`secondaryBackButton-${primaryIterator}`}
										aria-hidden={
											!(
												secondMenuToShow === primaryLink.title &&
												!thirdMenuToShow
											)
										}
										aria-label="Return to main links"
									>
										<Arrow
											rotation={0}
											withBorder={false}
											fill={Colours.SUNSHINE_YELLOW}
										/>
									</button>
									{secondMenuToShow === primaryLink.title
										? secondMenuToShow
										: null}
								</span>
								<ul className={styles.secondarynavlist}>
									{primaryLink.secondLevelItems.map(
										(secondaryLink, secondaryIterator) => (
											<li
												key={secondaryLink.title}
												className={
													thirdMenuToShow === secondaryLink.title
														? styles.secondarylinkactive
														: styles.secondarylink
												}
											>
												<DataLayerWrapper
													event={"menu_navigation"}
													menu_level={"sub"}
													menu_clicked={secondaryLink.title}
													menu_url={secondaryLink.url}
												>
													<LinkNoStyle
														id={
															secondMenuToShow === primaryLink.title
																? `secondary-${secondaryIterator}`
																: null
														}
														tabIndex={
															secondMenuToShow === primaryLink.title &&
															!thirdMenuToShow
																? 0
																: -1
														}
														aria-hidden={
															!(
																secondMenuToShow === primaryLink.title &&
																!thirdMenuToShow
															)
														}
														to={secondaryLink.url}
														visible={!thirdMenuToShow}
														onKeyDown={(e) => {
															if (e.key === "Escape") {
																setSecondMenuToShow(null);
																setThirdMenuToShow(null);
															}
															if (
																e.key === "Tab" &&
																!e.shiftKey &&
																secondaryIterator ===
																	primaryLink.secondLevelItems.length - 1 &&
																secondaryLink.thirdLevelItems?.length === 0
															) {
																e.preventDefault();
																document
																	.getElementById(
																		`secondaryBackButton-${primaryIterator}`,
																	)
																	.focus();
															}
														}}
													>
														{secondaryLink.title}
													</LinkNoStyle>
												</DataLayerWrapper>
												{secondaryLink.thirdLevelItems?.length > 0 && (
													<button
														type="button"
														tabIndex={
															secondMenuToShow === primaryLink.title &&
															!thirdMenuToShow
																? 0
																: -1
														}
														onClick={() =>
															setThirdMenuToShow(
																thirdMenuToShow === secondaryLink.title
																	? null
																	: secondaryLink.title,
															)
														}
														onKeyDown={(e) => {
															if (e.key === "Escape") {
																setSecondMenuToShow(null);
																setThirdMenuToShow(null);
															}
															if (e.key === "Enter") {
																e.preventDefault();
																setThirdMenuToShow(
																	thirdMenuToShow === secondaryLink.title
																		? null
																		: secondaryLink.title,
																);
															}
															if (
																e.key === "Tab" &&
																!e.shiftKey &&
																secondaryIterator ===
																	primaryLink.secondLevelItems.length - 1
															) {
																e.preventDefault();
																document
																	.getElementById(
																		`secondaryBackButton-${primaryIterator}`,
																	)
																	.focus();
															}
														}}
														style={{
															visibility: thirdMenuToShow
																? "hidden"
																: "visible",
														}}
														className={styles.subMenuNavArrows}
														aria-label={`See more links for ${secondaryLink.title}`}
														aria-hidden={
															!(
																secondMenuToShow === primaryLink.title &&
																!thirdMenuToShow
															)
														}
													>
														<Arrow
															rotation={
																thirdMenuToShow === secondaryLink.title
																	? 0
																	: 180
															}
															withBorder={false}
															fill={
																thirdMenuToShow === secondaryLink.title
																	? Colours.DAWN
																	: Colours.SUNSHINE_YELLOW
															}
														/>
													</button>
												)}
												{/* THE TERTIARY NAV MENU */}
												{secondaryLink.thirdLevelItems?.length > 0 && (
													<div
														className={
															thirdMenuToShow === secondaryLink.title
																? styles.tertiarynavwrapper
																: styles.secondarynavhidden
														}
														style={{
															top: headerHeight,
														}}
														aria-haspopup={false}
														aria-hidden={
															thirdMenuToShow !== secondaryLink.title
														}
													>
														<span className={styles.secondarytitle}>
															<button
																type="button"
																onClick={() => {
																	setThirdMenuToShow(null);
																}}
																tabIndex={
																	thirdMenuToShow === secondaryLink.title
																		? 0
																		: -1
																}
																className={cx(
																	styles.backarrow,
																	styles.subMenuNavArrows,
																)}
																onKeyDown={(e) => {
																	if (e.key === "Escape") {
																		setSecondMenuToShow(primaryLink.title);
																		setThirdMenuToShow(null);
																	}
																	if (e.key === "Enter") {
																		e.preventDefault();
																		setSecondMenuToShow(primaryLink.title);
																		setThirdMenuToShow(null);
																		document
																			.getElementById("burger-menu")
																			.focus();
																	}
																}}
																id={
																	thirdMenuToShow === secondaryLink.title
																		? `tertiaryBackButton-${secondaryIterator}`
																		: null
																}
																aria-label={`Return to ${secondaryLink.title} links`}
															>
																<Arrow
																	rotation={0}
																	withBorder={false}
																	fill={Colours.SUNSHINE_YELLOW}
																/>
															</button>
															{thirdMenuToShow}
														</span>
														<ul className={styles.tertiarynavlist}>
															{secondaryLink.thirdLevelItems?.map(
																(tertiaryLink, j) => (
																	<li
																		key={tertiaryLink.title}
																		className={styles.tertiarylink}
																	>
																		<DataLayerWrapper
																			event={"menu_navigation"}
																			menu_level={"sub"}
																			menu_clicked={tertiaryLink.title}
																			menu_url={tertiaryLink.url}
																		>
																			<LinkNoStyle
																				id={
																					thirdMenuToShow ===
																					secondaryLink.title
																						? `tertiary-${j}`
																						: null
																				}
																				tabIndex={
																					thirdMenuToShow ===
																					secondaryLink.title
																						? 0
																						: -1
																				}
																				to={tertiaryLink.url}
																				onKeyDown={(e) => {
																					if (e.key === "Escape") {
																						setSecondMenuToShow(null);
																						setThirdMenuToShow(null);
																					}
																					if (
																						e.key === "Tab" &&
																						!e.shiftKey &&
																						j ===
																							tertiaryLink.thirdLevelItems
																								?.length -
																								1
																					) {
																						e.preventDefault();
																						document
																							.getElementById(
																								`tertiaryBackButton-${secondaryIterator}`,
																							)
																							.focus();
																					}
																				}}
																			>
																				{tertiaryLink.title}
																			</LinkNoStyle>
																		</DataLayerWrapper>
																	</li>
																),
															)}
														</ul>
													</div>
												)}
											</li>
										),
									)}
								</ul>
							</div>
						)}
					</li>
				))}
			</ul>
		</nav>
	);
};

export default BurgerMenuMobile;
